import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldAlert, Download, Filter, Search, Ban, AlertCircle,
  Building2, User2, Layers, X, RefreshCw, ChevronLeft, ChevronRight,
  Bell, Settings2,
} from "lucide-react";
import DateInputBR from "@/components/DateInputBR";
import { toast } from "sonner";

const PAGE_SIZE = 50;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminBlocksPage() {
  const { companies } = useCompany();
  const [catalog, setCatalog] = useState(null);
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertSettings, setAlertSettings] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const [filters, setFilters] = useState({
    user_id: "",
    role: "",
    module: "",
    action: "",
    company_id: "",
    date_from: "",
    date_to: "",
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const loadCatalog = useCallback(async () => {
    try {
      const { data: c } = await api.get("/permissions/catalog");
      setCatalog(c);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = queryString + (queryString ? "&" : "") + `limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}`;
      const [{ data: d }, { data: s }] = await Promise.all([
        api.get(`/permissions/denials?${qs}`),
        api.get(`/permissions/denials/timeseries?${queryString}`),
      ]);
      setData(d);
      setSeries(s.series || []);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao carregar bloqueios");
      setData({ items: [], total: 0, aggregations: { top_users: [], top_modules: [] } });
    } finally { setLoading(false); }
  }, [queryString, page]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { load(); }, [load]);

  const itemsFiltered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    if (!s) return data.items;
    return data.items.filter((it) =>
      (it.user_email || "").toLowerCase().includes(s)
      || (it.user_name || "").toLowerCase().includes(s)
      || (it.endpoint || "").toLowerCase().includes(s)
      || (it.module || "").toLowerCase().includes(s)
      || (it.action || "").toLowerCase().includes(s)
    );
  }, [data, search]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const clearFilters = () => {
    setFilters({ user_id: "", role: "", module: "", action: "", company_id: "", date_from: "", date_to: "" });
    setPage(0);
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/permissions/denials/export.csv?${queryString}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Falha ao exportar");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bloqueios_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error(ex.message || "Falha ao exportar");
    }
  };

  const maxCount = Math.max(1, ...series.map((s) => s.count));

  return (
    <div className="space-y-6" data-testid="admin-blocks-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Administração · Segurança</div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <ShieldAlert className="h-7 w-7 text-[#A8261F]" /> Trilha de Bloqueios
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Toda tentativa de acesso negada pelos guards granulares é registrada aqui — usuário, endpoint,
            módulo, ação, IP e data/hora. Use os filtros para investigar padrões e exportar em CSV.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const [{ data: s }, { data: a }] = await Promise.all([
                api.get("/permissions/security-alerts/settings"),
                api.get("/permissions/security-alerts?limit=20"),
              ]);
              setAlertSettings(s);
              setAlerts(a.items || []);
              setAlertsOpen(true);
            } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
          }} data-testid="blocks-alerts-btn">
            <Bell className="h-4 w-4 mr-1" /> Alertas de segurança
          </Button>
          <Button variant="outline" onClick={() => load()} data-testid="blocks-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
          <Button onClick={downloadCsv} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="blocks-export-csv-btn">
            <Download className="h-4 w-4 mr-1" /> Exportar CSV
          </Button>
        </div>
      </div>

      {/* KPIs topo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total no período" value={data?.total ?? 0} testId="kpi-total" tone="rose" icon={Ban} />
        <KpiCard label="Usuários distintos" value={data?.aggregations?.top_users?.length ?? 0} testId="kpi-users" tone="amber" icon={User2} />
        <KpiCard label="Módulos afetados" value={new Set((data?.items || []).map((i) => i.module)).size} testId="kpi-modules" tone="indigo" icon={Layers} />
        <KpiCard label="Faixa (dias)" value={series.length} testId="kpi-days" tone="slate" icon={Filter} />
      </div>

      {/* Filtros */}
      <div className="rounded-xl border border-border bg-white p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Perfil</label>
            <Select value={filters.role || "all"} onValueChange={(v) => { setFilters((f) => ({ ...f, role: v === "all" ? "" : v })); setPage(0); }}>
              <SelectTrigger data-testid="blocks-filter-role"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {catalog?.roles?.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Módulo</label>
            <Select value={filters.module || "all"} onValueChange={(v) => { setFilters((f) => ({ ...f, module: v === "all" ? "" : v })); setPage(0); }}>
              <SelectTrigger data-testid="blocks-filter-module"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Todos</SelectItem>
                {catalog?.modules?.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Ação</label>
            <Select value={filters.action || "all"} onValueChange={(v) => { setFilters((f) => ({ ...f, action: v === "all" ? "" : v })); setPage(0); }}>
              <SelectTrigger data-testid="blocks-filter-action"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {catalog?.actions?.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Empresa</label>
            <Select value={filters.company_id || "all"} onValueChange={(v) => { setFilters((f) => ({ ...f, company_id: v === "all" ? "" : v })); setPage(0); }}>
              <SelectTrigger data-testid="blocks-filter-company"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">De</label>
            <DateInputBR value={filters.date_from} onChange={(v) => { setFilters((f) => ({ ...f, date_from: v })); setPage(0); }} testId="blocks-filter-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Até</label>
            <DateInputBR value={filters.date_to} onChange={(v) => { setFilters((f) => ({ ...f, date_to: v })); setPage(0); }} testId="blocks-filter-to" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Buscar por e-mail, endpoint, módulo..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="blocks-search" />
            </div>
            <Button variant="outline" onClick={clearFilters} className="ml-2" data-testid="blocks-clear-filters">
              <X className="h-4 w-4 mr-1" /> Limpar
            </Button>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Bloqueios por período" testId="chart-period">
          {series.length === 0 ? (
            <div className="text-[13px] text-muted-foreground py-6 text-center">Nenhum bloqueio registrado.</div>
          ) : (
            <div className="flex items-end gap-1 h-40 mt-2 px-1">
              {series.map((s) => (
                <div key={s.date} className="flex-1 group relative">
                  <div
                    className="bg-rose-500 hover:bg-rose-600 rounded-t transition-colors"
                    style={{ height: `${(s.count / maxCount) * 100}%`, minHeight: "3px" }}
                    title={`${s.date}: ${s.count}`}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1 px-1">
            <span>{series[0]?.date || ""}</span>
            <span>{series.at(-1)?.date || ""}</span>
          </div>
        </ChartCard>

        <ChartCard title="Top módulos bloqueados" testId="chart-modules">
          {(data?.aggregations?.top_modules || []).length === 0 ? (
            <div className="text-[13px] text-muted-foreground py-6 text-center">Nenhum bloqueio registrado.</div>
          ) : (
            <div className="space-y-1.5 mt-2">
              {data.aggregations.top_modules.map((m) => {
                const maxM = Math.max(...data.aggregations.top_modules.map((x) => x.count));
                return (
                  <div key={`${m.module}-${m.action}`} className="flex items-center gap-2">
                    <div className="w-40 text-[12px] font-medium truncate" title={`${m.module}.${m.action}`}>
                      {m.module}.{m.action}
                    </div>
                    <div className="flex-1 bg-slate-100 h-4 rounded overflow-hidden">
                      <div className="bg-[#234935] h-full" style={{ width: `${(m.count / maxM) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right text-[12px] font-mono">{m.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Timeline */}
      <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="blocks-timeline">
        <div className="px-4 py-2.5 border-b border-border bg-slate-50 flex items-center justify-between">
          <div className="font-display text-[13.5px] font-semibold">Timeline · {itemsFiltered.length} de {data?.total ?? 0}</div>
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <Button variant="ghost" size="sm" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} data-testid="blocks-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            Página {page + 1} / {totalPages}
            <Button variant="ghost" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="blocks-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-muted-foreground text-[13px]">Carregando...</div>
        ) : itemsFiltered.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-muted-foreground">
            <ShieldAlert className="h-8 w-8 mx-auto mb-2 opacity-50" />
            Nenhum bloqueio registrado com os filtros atuais.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {itemsFiltered.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setDetail(it)}
                className="w-full text-left px-4 py-3 hover:bg-rose-50/30 flex items-start gap-3"
                data-testid={`block-row-${it.id}`}
              >
                <div className="h-8 w-8 rounded-full bg-rose-100 grid place-items-center shrink-0">
                  <Ban className="h-3.5 w-3.5 text-rose-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12.5px] font-medium">{it.user_name || it.user_email}</span>
                    <Badge variant="outline" className="text-[10px] py-0 bg-slate-50">{it.role}</Badge>
                    {(it.group_names || []).length > 0 && (
                      <Badge variant="outline" className="text-[10px] py-0 bg-emerald-50 text-emerald-800">
                        grupos: {it.group_names.join(", ")}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11.5px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{it.method} {it.endpoint}</span>
                    <span>·</span>
                    <span>exigia <b className="text-rose-700">{it.required}</b></span>
                    {it.ip && <><span>·</span><span>IP {it.ip}</span></>}
                    {it.company_id && <><span>·</span><span>empresa {it.company_id}</span></>}
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0 text-right">
                  <div>{fmtDate(it.created_at)}</div>
                  <Badge className="bg-red-100 text-red-800 border-red-200 border mt-1">403</Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Alertas de segurança */}
      <Dialog open={alertsOpen} onOpenChange={setAlertsOpen}>
        <DialogContent className="max-w-3xl" data-testid="alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-700" /> Alertas de Segurança
            </DialogTitle>
            <DialogDescription>
              Master é notificado por e-mail quando um usuário acumula N bloqueios em uma janela de tempo.
              Ação registrada em auditoria.
            </DialogDescription>
          </DialogHeader>
          {alertSettings && (
            <div className="rounded-lg border border-border p-4 bg-slate-50 space-y-3" data-testid="alerts-settings">
              <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Settings2 className="h-3.5 w-3.5" /> Configurações
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <label className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={!!alertSettings.enabled}
                    onChange={(e) => setAlertSettings({ ...alertSettings, enabled: e.target.checked })}
                    data-testid="alerts-enabled" />
                  Ativo
                </label>
                <div>
                  <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground block mb-1">Bloqueios (threshold)</label>
                  <Input type="number" min={1} max={1000} value={alertSettings.threshold}
                    onChange={(e) => setAlertSettings({ ...alertSettings, threshold: +e.target.value })}
                    data-testid="alerts-threshold" />
                </div>
                <div>
                  <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground block mb-1">Janela (min)</label>
                  <Input type="number" min={1} max={1440} value={alertSettings.window_minutes}
                    onChange={(e) => setAlertSettings({ ...alertSettings, window_minutes: +e.target.value })}
                    data-testid="alerts-window" />
                </div>
                <div>
                  <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground block mb-1">Cooldown (min)</label>
                  <Input type="number" min={0} max={1440} value={alertSettings.cooldown_minutes}
                    onChange={(e) => setAlertSettings({ ...alertSettings, cooldown_minutes: +e.target.value })}
                    data-testid="alerts-cooldown" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" className="bg-[#234935] hover:bg-[#2c5c43]"
                  onClick={async () => {
                    try {
                      const { data } = await api.put("/permissions/security-alerts/settings", alertSettings);
                      setAlertSettings(data);
                      toast.success("Configuração salva");
                    } catch (ex) {
                      toast.error(formatApiError(ex.response?.data?.detail));
                    }
                  }}
                  data-testid="alerts-save-settings"
                >Salvar configuração</Button>
              </div>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-2 mt-2">
              Últimos alertas ({alerts.length})
            </div>
            <div className="max-h-[40vh] overflow-y-auto border border-slate-200 rounded-md">
              {alerts.length === 0 ? (
                <div className="p-6 text-center text-[13px] text-muted-foreground">
                  <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" />
                  Nenhum alerta disparado.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Usuário</TableHead>
                      <TableHead className="w-[120px]">Bloqueios</TableHead>
                      <TableHead className="w-[140px]">Gatilho</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map((a) => (
                      <TableRow key={a.id} data-testid={`alert-row-${a.id}`}>
                        <TableCell className="text-[12px] font-mono">{fmtDate(a.created_at)}</TableCell>
                        <TableCell className="text-[12.5px]">{a.user_email}</TableCell>
                        <TableCell className="text-[12.5px]"><b>{a.count_in_window}</b> em {a.window_minutes}min</TableCell>
                        <TableCell className="text-[11.5px] font-mono">{a.trigger_module}.{a.trigger_action}</TableCell>
                        <TableCell>
                          <Badge className={
                            a.status === "sent" ? "bg-emerald-100 text-emerald-800 border-emerald-200 border"
                            : "bg-slate-100 text-slate-700 border-slate-200 border"
                          }>
                            {a.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detalhe */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid="block-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-rose-700" /> Detalhes da tentativa negada
            </DialogTitle>
            <DialogDescription>Registro imutável da trilha de segurança.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-[13px]">
              <Row label="Data / hora" value={fmtDate(detail.created_at)} />
              <Row label="Usuário" value={`${detail.user_name || "—"} · ${detail.user_email}`} />
              <Row label="Perfil" value={detail.role} />
              <Row label="Grupos" value={(detail.group_names || []).join(", ") || "—"} />
              <Row label="Módulo" value={detail.module} />
              <Row label="Ação tentada" value={detail.action} />
              <Row label="Permissão exigida" value={<code className="bg-rose-50 text-rose-900 px-1.5 py-0.5 rounded">{detail.required}</code>} />
              <Row label="Endpoint" value={<code className="font-mono text-[11.5px]">{detail.method} {detail.endpoint}</code>} />
              <Row label="Empresa" value={detail.company_id || "—"} />
              <Row label="IP" value={detail.ip || "—"} />
              <Row label="User-Agent" value={<span className="text-[11.5px] break-all">{detail.user_agent || "—"}</span>} />
              <Row label="Resultado" value={<Badge className="bg-red-100 text-red-800 border-red-200 border">HTTP {detail.result}</Badge>} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, testId, tone = "slate", icon: Icon }) {
  const tones = {
    rose:   "bg-rose-50 text-rose-900 border-rose-100",
    amber:  "bg-amber-50 text-amber-900 border-amber-100",
    indigo: "bg-indigo-50 text-indigo-900 border-indigo-100",
    slate:  "bg-slate-50 text-slate-900 border-slate-100",
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${tones}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider opacity-70 font-medium">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {label}
      </div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 border-b border-slate-100 pb-1.5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
      <div>{value}</div>
    </div>
  );
}
